import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { copyText } from "@/lib/clipboard";
import { FilePdf, LinkSimple, XLogo, LinkedinLogo, WhatsappLogo } from "@phosphor-icons/react";
import PublicShell from "@/components/PublicShell";
import { HpiReportView } from "@/components/HpiReportView";
import { API } from "@/lib/api";

function Share({ month }) {
  const url = `${window.location.origin}/hpi/${month}`;
  const text = encodeURIComponent(`Sliply Household Price Index — ${month}`);
  const u = encodeURIComponent(url);
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold hover:border-riq-primary hover:text-riq-primary transition-colors";
  return (
    <div className="flex flex-wrap gap-2" data-testid="hpi-share">
      <a data-testid="hpi-download-pdf" href={`${API}/public/hpi/${month}/pdf`} target="_blank" rel="noreferrer" className={`${btn} !bg-riq-primary !text-white !border-riq-primary`}><FilePdf size={14} weight="bold" /> Download PDF</a>
      <button data-testid="hpi-copy-link" className={btn} onClick={() => copyText(url, "Link copied")}><LinkSimple size={14} /> Copy link</button>
      <a data-testid="hpi-share-x" className={btn} target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${text}&url=${u}`}><XLogo size={14} /> Post</a>
      <a data-testid="hpi-share-linkedin" className={btn} target="_blank" rel="noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`}><LinkedinLogo size={14} /> LinkedIn</a>
      <a data-testid="hpi-share-whatsapp" className={btn} target="_blank" rel="noreferrer" href={`https://wa.me/?text=${text}%20${u}`}><WhatsappLogo size={14} /> WhatsApp</a>
    </div>
  );
}

export default function PublicHPI() {
  const { month } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    setState({ loading: true });
    Promise.all([
      fetch(`${API}/public/hpi`).then((r) => r.json()),
      month ? fetch(`${API}/public/hpi/${month}`).then((r) => (r.ok ? r.json() : null)) : Promise.resolve(undefined),
    ]).then(([idx, one]) => setState({ archive: idx.archive || [], report: month ? one : idx.latest }))
      .catch(() => setState({ archive: [], report: null }));
  }, [month]);

  const { loading, archive, report } = state;
  return (
    <PublicShell testId="public-hpi">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-12">
        {loading ? <div className="py-24 text-center text-slate-500">Loading…</div> : !report ? (
          <div data-testid="hpi-empty" className="py-24 text-center">
            <h1 className="font-head text-4xl font-extrabold">Sliply Household Price Index</h1>
            <p className="mt-3 text-slate-500">{month ? "This month's report isn't published." : "The first monthly report is coming soon."}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_220px] gap-10">
            <div className="min-w-0">
              <div className="mb-6"><Share month={report.month} /></div>
              <HpiReportView report={report} />
            </div>
            <aside data-testid="hpi-archive" className="lg:sticky lg:top-24 h-fit">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Archive</div>
              <div className="space-y-1">
                {archive.map((a) => (
                  <Link key={a.month} data-testid={`hpi-archive-${a.month}`} to={`/hpi/${a.month}`}
                    className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${a.month === report.month ? "bg-riq-primary text-white" : "hover:bg-slate-100"}`}>
                    <span>{a.month}</span><span className="tabular-nums font-semibold">{a.headline?.index?.toFixed(1) ?? "—"}</span>
                  </Link>
                ))}
              </div>
            </aside>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
